import { useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { CheckCircle, XCircle, ArrowDown, BellRinging, Clock, Airplane, ClockCounterClockwise, ArrowsClockwise } from "@phosphor-icons/react";
import { DateInput } from "../components/ui/date-input";

function ageLabel(hours) {
  if (hours == null) return "—";
  const h = Number(hours);
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 24) return `${Math.round(h)}h`;
  return `${Math.round(h / 24)}d`;
}

function ageTone(hours, overdue) {
  if (overdue) return "bg-red-50 text-red-700 border-red-200";
  if (hours == null) return "bg-zinc-50 text-zinc-600 border-zinc-200";
  if (hours < 4) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (hours < 24) return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-orange-50 text-orange-700 border-orange-200";
}

export default function Approvals() {
  const [inbox, setInbox] = useState([]);
  const [mine, setMine] = useState([]);
  const [sortBy, setSortBy] = useState("aging"); // aging | newest
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState({}); // { approvalId: true }
  const [bulkOpen, setBulkOpen] = useState(null); // 'approve' | 'reject'
  const [bulkComment, setBulkComment] = useState("");
  const [bulkRejCode, setBulkRejCode] = useState("");
  const [oooOpen, setOooOpen] = useState(false);
  const [ooo, setOoo] = useState(null);
  const [users, setUsers] = useState([]);
  const [rejCodes, setRejCodes] = useState([]);
  const [auditOpen, setAuditOpen] = useState(null); // approval id

  const load = async () => {
    const [i, m, codes] = await Promise.all([
      api.get("/approvals?status=all"),
      api.get("/approvals/mine"),
      api.get("/approvals/rejection-codes").catch(() => ({ data: [] })),
    ]);
    setInbox(i.data); setMine(m.data); setRejCodes(codes.data || []);
  };
  const loadOoo = async () => {
    const [o, u] = await Promise.all([
      api.get("/approvals/me/ooo"),
      api.get("/employees").catch(() => ({ data: [] })),
    ]);
    setOoo(o.data);
    setUsers((u.data || []).filter((e) => e.user_id).map((e) => ({ id: e.user_id, name: e.name })));
  };
  useEffect(() => { load(); loadOoo(); }, []);

  // Selection logic — only my-turn pending items can be bulk-decided
  const myTurn = inbox.filter((a) => a.is_my_turn);
  const sortedMyTurn = useMemo(() => {
    const arr = [...myTurn];
    if (filter) {
      const q = filter.toLowerCase();
      arr.filter((a) => a.title?.toLowerCase().includes(q) || a.requester_name?.toLowerCase().includes(q));
    }
    arr.sort((a, b) => {
      if (sortBy === "aging") return (b.aging_hours || 0) - (a.aging_hours || 0);
      return new Date(b.created_at) - new Date(a.created_at);
    });
    return arr.filter((a) => !filter || a.title?.toLowerCase().includes(filter.toLowerCase()) || a.requester_name?.toLowerCase().includes(filter.toLowerCase()));
  }, [myTurn, sortBy, filter]);

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const allSelected = sortedMyTurn.length > 0 && sortedMyTurn.every((a) => selected[a.id]);
  const toggleAll = () => {
    if (allSelected) setSelected({});
    else setSelected(Object.fromEntries(sortedMyTurn.map((a) => [a.id, true])));
  };

  const doBulk = async () => {
    if (!selectedIds.length) return toast.error("Select at least one request");
    if (bulkOpen === "reject" && !bulkRejCode) return toast.error("Pick a rejection reason");
    try {
      const r = await api.post("/approvals/bulk-decide", {
        approval_ids: selectedIds,
        decision: bulkOpen,
        comment: bulkComment,
        rejection_code: bulkOpen === "reject" ? bulkRejCode : null,
      });
      toast.success(`Applied to ${r.data.applied.length}, skipped ${r.data.skipped.length}`);
      setSelected({}); setBulkOpen(null); setBulkComment(""); setBulkRejCode("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const myOoo = ooo?.is_active && ooo?.delegatee_user_id;

  return (
    <AppShell title="Approvals">

      {myOoo && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-center justify-between gap-3" data-testid="ooo-banner">
          <div className="flex items-center gap-2 text-sm">
            <Airplane size={18} weight="duotone" className="text-amber-700 shrink-0" />
            <span className="text-amber-900">
              You're <strong>out of office</strong> — new approvals route to <strong>{ooo.delegatee_name}</strong>.
            </span>
          </div>
          <Button size="sm" variant="outline" className="border-amber-300" onClick={() => setOooOpen(true)} data-testid="ooo-edit-banner">Edit</Button>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap" data-testid="approvals-toolbar">
        <div className="flex items-center gap-2 flex-wrap">
          <Input placeholder="Filter by title or requester…" value={filter} onChange={(e) => setFilter(e.target.value)} className="h-9 w-64" data-testid="approvals-filter" />
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-44 h-9" data-testid="approvals-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="aging">Sort: oldest first</SelectItem>
              <SelectItem value="newest">Sort: newest first</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" className="border-zinc-300" onClick={() => setOooOpen(true)} data-testid="ooo-open-btn">
          <Airplane size={14} className="mr-1.5" /> {myOoo ? "Out of office" : "Set out of office"}
        </Button>
      </div>

      <Tabs defaultValue="inbox" className="w-full" data-testid="approvals-tabs">
        <TabsList>
          <TabsTrigger value="inbox" data-testid="tab-inbox">Inbox ({myTurn.length})</TabsTrigger>
          <TabsTrigger value="mine" data-testid="tab-mine">Submitted ({mine.length})</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">All I'm involved in ({inbox.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="inbox">
          {sortedMyTurn.length > 0 && (
            <div className="flex items-center justify-between gap-3 mt-4 mb-2 px-1" data-testid="bulk-toolbar">
              <div className="flex items-center gap-2">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-cb" />
                <span className="text-sm text-zinc-600">
                  {selectedIds.length > 0 ? `${selectedIds.length} selected` : "Select all"}
                </span>
              </div>
              {selectedIds.length > 0 && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => setBulkOpen("reject")} data-testid="bulk-reject-btn">
                    <XCircle size={14} className="mr-1.5" /> Reject {selectedIds.length}
                  </Button>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setBulkOpen("approve")} data-testid="bulk-approve-btn">
                    <CheckCircle size={14} className="mr-1.5" /> Approve {selectedIds.length}
                  </Button>
                </div>
              )}
            </div>
          )}
          <ApprovalsList items={sortedMyTurn} canAct selected={selected} setSelected={setSelected}
                         rejCodes={rejCodes} onRefresh={load} onAudit={(id) => setAuditOpen(id)}
                         empty="Inbox zero." />
        </TabsContent>
        <TabsContent value="mine">
          <ApprovalsList items={mine} rejCodes={rejCodes} onRefresh={load}
                         showRemind onAudit={(id) => setAuditOpen(id)}
                         empty="You haven't submitted anything." />
        </TabsContent>
        <TabsContent value="all">
          <ApprovalsList items={inbox} rejCodes={rejCodes} onRefresh={load}
                         onAudit={(id) => setAuditOpen(id)} empty="Nothing here." />
        </TabsContent>
      </Tabs>

      {/* Bulk dialog */}
      <Dialog open={!!bulkOpen} onOpenChange={(v) => !v && setBulkOpen(null)}>
        <DialogContent data-testid="bulk-dialog">
          <DialogHeader><DialogTitle>{bulkOpen === "approve" ? "Approve" : "Reject"} {selectedIds.length} request{selectedIds.length === 1 ? "" : "s"}</DialogTitle></DialogHeader>
          {bulkOpen === "reject" && (
            <div className="mt-2">
              <Label>Reason</Label>
              <Select value={bulkRejCode} onValueChange={setBulkRejCode}>
                <SelectTrigger className="mt-2" data-testid="bulk-reason"><SelectValue placeholder="Pick a reason" /></SelectTrigger>
                <SelectContent>
                  {rejCodes.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <Textarea className="mt-3" placeholder={bulkOpen === "approve" ? "Optional comment" : "Additional context"}
                    value={bulkComment} onChange={(e) => setBulkComment(e.target.value)} data-testid="bulk-comment" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(null)}>Cancel</Button>
            <Button onClick={doBulk}
                    className={bulkOpen === "approve" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"}
                    data-testid="bulk-confirm">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* OOO dialog */}
      <Dialog open={oooOpen} onOpenChange={setOooOpen}>
        <DialogContent data-testid="ooo-dialog">
          <DialogHeader><DialogTitle>Out of Office delegation</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600">While you're away, your approval queue routes to a delegatee. They'll see your pending items in their inbox immediately.</p>
          <OooEditor ooo={ooo} users={users} onSaved={async (next) => { setOoo(next); setOooOpen(false); await load(); }} />
        </DialogContent>
      </Dialog>

      {/* Audit dialog */}
      <Dialog open={!!auditOpen} onOpenChange={(v) => !v && setAuditOpen(null)}>
        <DialogContent className="max-w-xl" data-testid="audit-dialog">
          <DialogHeader><DialogTitle>Audit trail</DialogTitle></DialogHeader>
          {auditOpen && <AuditTrail approvalId={auditOpen} />}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function ApprovalsList({ items, canAct, selected, setSelected, rejCodes, onRefresh, onAudit, showRemind, empty }) {
  if (!items.length) return <div className="py-12 text-center text-zinc-500" data-testid="approvals-empty">{empty}</div>;
  return (
    <div className="space-y-4 mt-4">
      {items.map((a) => (
        <ApprovalCard key={a.id} ap={a}
                      canAct={canAct && a.is_my_turn}
                      selected={!!(selected && selected[a.id])}
                      onToggleSelect={canAct ? () => setSelected({ ...selected, [a.id]: !selected[a.id] }) : null}
                      rejCodes={rejCodes}
                      onRefresh={onRefresh} onAudit={() => onAudit && onAudit(a.id)}
                      showRemind={showRemind} />
      ))}
    </div>
  );
}

function ApprovalCard({ ap, canAct, selected, onToggleSelect, rejCodes, onRefresh, onAudit, showRemind }) {
  const [comment, setComment] = useState("");
  const [rejCode, setRejCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const [reminding, setReminding] = useState(false);

  const decide = async (decision) => {
    if (decision === "reject" && !rejCode) {
      toast.error("Pick a rejection reason");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/approvals/${ap.id}/decide`, {
        decision, comment,
        rejection_code: decision === "reject" ? rejCode : null,
      });
      toast.success(`Request ${decision === "approve" ? "approved" : "rejected"}`);
      setOpen(null); setComment(""); setRejCode("");
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remind = async () => {
    setReminding(true);
    try {
      const r = await api.post(`/approvals/${ap.id}/remind`);
      toast.success(`Reminded ${r.data.reminded}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setReminding(false); }
  };

  return (
    <div className={`bg-white border rounded-lg p-6 ${ap.overdue ? "border-red-300" : "border-zinc-200"}`} data-testid={`approval-card-${ap.id}`}>
      <div className="flex items-start justify-between gap-6">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          {onToggleSelect && (
            <div className="pt-1">
              <Checkbox checked={selected} onCheckedChange={onToggleSelect} data-testid={`select-cb-${ap.id}`} />
            </div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <StatusPill status={ap.status} />
              <Badge variant="outline" className="uppercase text-[10px]">{ap.request_type.replace("_", " ")}</Badge>
              {ap.status === "pending" && (
                <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider border px-2 py-0.5 rounded-full ${ageTone(ap.aging_hours, ap.overdue)}`} data-testid={`age-pill-${ap.id}`}>
                  <Clock size={10} weight="bold" /> {ap.overdue ? "Overdue · " : ""}{ageLabel(ap.aging_hours)}
                </span>
              )}
              <span className="text-xs text-zinc-500">Step {ap.current_step} of {ap.steps.length}</span>
              {ap.workflow_name && <span className="text-xs text-zinc-400">· {ap.workflow_name}</span>}
            </div>
            <div className="font-display font-semibold text-lg mt-3">{ap.title}</div>
            <div className="text-sm text-zinc-600 mt-1">Raised by {ap.requester_name} · {new Date(ap.created_at).toLocaleDateString()}</div>
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          {canAct && ap.status === "pending" && (
            <div className="flex gap-2" data-testid={`approval-actions-${ap.id}`}>
              <Dialog open={open === "reject"} onOpenChange={(v) => setOpen(v ? "reject" : null)}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="rounded-md border-zinc-300" data-testid={`reject-btn-${ap.id}`}>
                    <XCircle size={16} className="mr-1.5" /> Reject
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Reject request</DialogTitle></DialogHeader>
                  <Label className="mt-2">Reason</Label>
                  <Select value={rejCode} onValueChange={setRejCode}>
                    <SelectTrigger className="mt-2" data-testid="reject-code"><SelectValue placeholder="Pick a reason" /></SelectTrigger>
                    <SelectContent>
                      {rejCodes.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Label className="mt-3">Comment</Label>
                  <Textarea className="mt-2" placeholder="Additional context for the requester" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="reject-comment" />
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(null)}>Cancel</Button>
                    <Button disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" onClick={() => decide("reject")} data-testid="reject-confirm">Confirm reject</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <Dialog open={open === "approve"} onOpenChange={(v) => setOpen(v ? "approve" : null)}>
                <DialogTrigger asChild>
                  <Button className="bg-zinc-950 hover:bg-zinc-800 rounded-md" data-testid={`approve-btn-${ap.id}`}>
                    <CheckCircle size={16} className="mr-1.5" /> Approve
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Approve request</DialogTitle></DialogHeader>
                  <Textarea className="mt-2" placeholder="Optional comment" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="approve-comment" />
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(null)}>Cancel</Button>
                    <Button disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => decide("approve")} data-testid="approve-confirm">Confirm approval</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
          {showRemind && ap.status === "pending" && (
            <Button variant="outline" size="sm" className="border-zinc-300" disabled={reminding} onClick={remind} data-testid={`remind-btn-${ap.id}`}>
              <BellRinging size={14} className="mr-1.5" /> {reminding ? "Sending…" : "Remind approver"}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onAudit} data-testid={`audit-btn-${ap.id}`}>
            <ClockCounterClockwise size={14} className="mr-1.5" /> Audit trail
          </Button>
        </div>
      </div>

      {/* Approval timeline */}
      <div className="mt-6 border-t border-zinc-100 pt-5" data-testid={`approval-timeline-${ap.id}`}>
        <div className="tiny-label">Approval chain</div>
        <ol className="mt-3 space-y-3">
          {ap.steps.map((s, i) => (
            <li key={i} className="flex items-start gap-3">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs shrink-0 ${
                s.status === "approved" ? "bg-emerald-500 text-white" :
                s.status === "rejected" ? "bg-red-500 text-white" :
                s.step === ap.current_step && ap.status === "pending" ? "bg-zinc-950 text-white" :
                "bg-zinc-100 text-zinc-500 border border-zinc-200"
              }`}>
                {s.status === "approved" ? "✓" : s.status === "rejected" ? "✕" : s.step}
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium flex items-center gap-2">
                  {s.approver_name}
                  {s.delegated && <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200"><ArrowsClockwise size={9} className="mr-0.5" /> Delegated</Badge>}
                </div>
                <div className="text-xs text-zinc-500 capitalize">
                  {s.approver_role.replace(/_/g, " ")} ·{" "}
                  <span className={`uppercase tracking-wider font-medium ${
                    s.status === "approved" ? "text-emerald-600" :
                    s.status === "rejected" ? "text-red-600" : "text-zinc-500"}`}>{s.status}</span>
                  {s.rejection_code && <span className="ml-2 text-red-600">· {s.rejection_code.replace(/_/g, " ")}</span>}
                </div>
                {s.comment && <div className="text-xs text-zinc-600 mt-1 italic">"{s.comment}"</div>}
              </div>
              {i < ap.steps.length - 1 && <ArrowDown size={12} className="text-zinc-300 mt-2" />}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function OooEditor({ ooo, users, onSaved }) {
  const [active, setActive] = useState(!!ooo?.is_active);
  const [delegatee, setDelegatee] = useState(ooo?.delegatee_user_id || "");
  const [reason, setReason] = useState(ooo?.reason || "");
  const [startsAt, setStartsAt] = useState(ooo?.starts_at?.slice(0, 10) || "");
  const [endsAt, setEndsAt] = useState(ooo?.ends_at?.slice(0, 10) || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (active && !delegatee) return toast.error("Pick a delegatee");
    setBusy(true);
    try {
      if (!active && ooo?.id) {
        await api.delete("/approvals/me/ooo");
        toast.success("OOO cleared");
        onSaved({ is_active: false, delegatee_user_id: null });
      } else {
        const r = await api.put("/approvals/me/ooo", {
          delegatee_user_id: delegatee,
          delegatee_name: users.find((u) => u.id === delegatee)?.name,
          starts_at: startsAt ? new Date(startsAt).toISOString() : null,
          ends_at: endsAt ? new Date(endsAt).toISOString() : null,
          is_active: active,
          reason,
        });
        toast.success("OOO saved");
        onSaved(r.data);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <Switch checked={active} onCheckedChange={setActive} data-testid="ooo-active" />
        <Label className="cursor-pointer" onClick={() => setActive(!active)}>I'm out of office — route my approvals</Label>
      </div>
      {active && (
        <>
          <div>
            <Label>Delegate to</Label>
            <Select value={delegatee} onValueChange={setDelegatee}>
              <SelectTrigger className="mt-2" data-testid="ooo-delegatee"><SelectValue placeholder="Pick a colleague" /></SelectTrigger>
              <SelectContent>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>From (optional)</Label>
              <DateInput className="mt-2" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} data-testid="ooo-start" />
            </div>
            <div>
              <Label>To (optional)</Label>
              <DateInput className="mt-2" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} data-testid="ooo-end" />
            </div>
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Input className="mt-2" placeholder="e.g. Annual leave" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="ooo-reason" />
          </div>
        </>
      )}
      <DialogFooter>
        <Button disabled={busy} onClick={save} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="ooo-save">{busy ? "Saving…" : "Save"}</Button>
      </DialogFooter>
    </div>
  );
}

function AuditTrail({ approvalId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/approvals/${approvalId}/audit`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [approvalId]);
  if (rows === null) return <div className="py-6 text-center text-zinc-500 text-sm">Loading…</div>;
  if (!rows.length) return <div className="py-6 text-center text-zinc-500 text-sm">No audit entries yet.</div>;
  const tone = {
    created: "bg-blue-50 text-blue-700 border-blue-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-red-50 text-red-700 border-red-200",
    reminded: "bg-amber-50 text-amber-700 border-amber-200",
    escalated: "bg-orange-50 text-orange-700 border-orange-200",
    delegated: "bg-violet-50 text-violet-700 border-violet-200",
  };
  return (
    <div className="space-y-3 mt-2" data-testid="audit-list">
      {rows.map((r) => (
        <div key={r.id} className="flex items-start gap-3 pb-3 border-b border-zinc-100 last:border-0">
          <span className={`text-[10px] uppercase tracking-wider border px-2 py-0.5 rounded-full font-semibold ${tone[r.action] || "bg-zinc-50 border-zinc-200 text-zinc-700"}`}>{r.action}</span>
          <div className="flex-1 min-w-0">
            <div className="text-sm">{r.actor_name}</div>
            <div className="text-xs text-zinc-500">{new Date(r.at).toLocaleString()}</div>
            {r.data && Object.keys(r.data).length > 0 && (
              <div className="text-xs text-zinc-600 mt-1">
                {Object.entries(r.data).filter(([, v]) => v != null && v !== "").map(([k, v]) =>
                  <span key={k} className="inline-block mr-3"><span className="text-zinc-400">{k}:</span> {typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-red-50 text-red-700 border-red-200",
  };
  return <span className={`text-[10px] uppercase tracking-wider border px-2 py-0.5 rounded-full ${map[status] || "bg-zinc-50 border-zinc-200 text-zinc-600"}`}>{status}</span>;
}
